import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getAuthToken, getRefreshToken, updateSessionTokens } from './session';

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  console.debug('api: request', `${config.method?.toUpperCase() ?? 'GET'} ${config.url}`);
  const token = getAuthToken();
  if (token && !config.headers?.Authorization) {
    const headers =
      config.headers instanceof AxiosHeaders
        ? config.headers
        : AxiosHeaders.from(config.headers as Record<string, string> | undefined);
    headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
  }
  return config;
};

const onRequestError = (error: AxiosError): Promise<never> => {
  console.warn('api: request error', error.message);
  return Promise.reject(error);
};

const onResponse = <T>(response: AxiosResponse<T>): AxiosResponse<T> => {
  console.debug('api: response', response.status, response.config.url);
  return response;
};

export const setupInterceptors = (client: AxiosInstance): void => {
  let refreshPromise: Promise<string | null> | null = null;

  const refreshAccessToken = async (): Promise<string | null> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;

    const refreshClient = axios.create({
      baseURL: client.defaults.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const response = await refreshClient.post('/auth/refresh', {
      refreshToken,
    });
    const data = response.data as { token?: string; refreshToken?: string };
    if (data?.token) {
      updateSessionTokens(data.token, data.refreshToken);
      return data.token;
    }
    return null;
  };

  const onResponseError = async (error: AxiosError): Promise<never | AxiosResponse> => {
    const status = error.response?.status;
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    console.error('api: response error', status, error.message);

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken();
        }
        const newToken = await refreshPromise;
        refreshPromise = null;

        if (newToken) {
          const headers =
            originalRequest.headers instanceof AxiosHeaders
              ? originalRequest.headers
              : AxiosHeaders.from(
                  originalRequest.headers as Record<string, string> | undefined,
                );
          headers.set('Authorization', `Bearer ${newToken}`);
          originalRequest.headers = headers;
          return client.request(originalRequest);
        }
      } catch (refreshError) {
        refreshPromise = null;
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  };

  client.interceptors.request.use(onRequest, onRequestError);
  client.interceptors.response.use(onResponse, onResponseError);
};
